import { Controller } from "@hotwired/stimulus"
import "cropperjs"

const ASPECT_RATIO = 4 / 3

export default class extends Controller {
  static targets = ["fileInput", "fileButton", "modal", "cropperArea", "cropDataField", "preview", "previewImage", "existingImage", "loading"]
  static values = {
    autoCropUrl: String,
    autoCropEnabled: Boolean,
    existingCropData: String,
    existingImageUrl: String
  }

  connect() {
    this.cropping = false
  }

  disconnect() {
    this.cleanup()
    if (this.selectedFileUrl) {
      URL.revokeObjectURL(this.selectedFileUrl)
      this.selectedFileUrl = null
    }
  }

  // カスタムボタンからファイル選択ダイアログを開く
  selectFile() {
    this.fileInputTarget.click()
  }

  // ファイル選択時
  async fileSelected(event) {
    const file = event.target.files[0]
    if (!file) return

    let processedFile = file

    // HEICファイルはJPEGに変換してから通常フローに合流
    if (this._isHeic(file)) {
      // 変換に時間がかかるので、先にモーダルを開いてローディング表示
      this.modalTarget.classList.remove("hidden")
      document.body.classList.add("overflow-hidden")
      this.showLoading("画像を変換中...")

      processedFile = await this._convertHeicToJpeg(file)
      this.hideLoading()

      if (!processedFile) {
        this.modalTarget.classList.add("hidden")
        document.body.classList.remove("overflow-hidden")
        this.fileInputTarget.value = ""
        alert("この画像ファイルを変換できませんでした。JPEGまたはPNG形式でアップロードしてください。")
        return
      }

      // ファイル入力をJPEGに差し替え（サーバーにはJPEGが届く）
      const dt = new DataTransfer()
      dt.items.add(processedFile)
      this.fileInputTarget.files = dt.files
    }

    this.selectedFile = processedFile
    this._pendingNewFile = true

    // ボタンテキストを「画像を変更」に更新
    if (this.hasFileButtonTarget) {
      this.fileButtonTarget.textContent = "画像を変更"
    }

    // 以降はPNG/JPGと完全に同じフロー
    if (this.selectedFileUrl) URL.revokeObjectURL(this.selectedFileUrl)
    this.selectedFileUrl = URL.createObjectURL(processedFile)
    await this.openModal(this.selectedFileUrl, null)
  }

  _isHeic(file) {
    if (file.type === "image/heic" || file.type === "image/heif") return true
    return /\.heic$/i.test(file.name) || /\.heif$/i.test(file.name)
  }

  // HEIC→JPEG変換（heic-to → ネイティブの順に試行）
  async _convertHeicToJpeg(file) {
    const jpegName = file.name.replace(/\.(heic|heif)$/i, ".jpg")

    // 1. heic-toライブラリ変換（libheif 1.21.2ベース、iOS 18対応）
    try {
      const { heicTo } = await import("heic-to")
      const blob = await heicTo({ blob: file, type: "image/jpeg", quality: 0.92 })
      return new File([blob], jpegName, { type: "image/jpeg" })
    } catch (e) {
      console.warn("heic-to conversion failed, trying native:", e)
    }

    // 2. ブラウザネイティブ変換（Safari, Chrome 122+のフォールバック）
    const nativeResult = await this._tryNativeConversion(file, jpegName)
    if (nativeResult) return nativeResult

    return null
  }

  _tryNativeConversion(file, jpegName) {
    return new Promise((resolve) => {
      const objectUrl = URL.createObjectURL(file)
      const img = new Image()
      const timer = setTimeout(() => { img.src = ""; URL.revokeObjectURL(objectUrl); resolve(null) }, 5000)

      img.onload = () => {
        clearTimeout(timer)
        const MAX_DIM = 2048
        let w = img.naturalWidth
        let h = img.naturalHeight
        if (w > MAX_DIM || h > MAX_DIM) {
          const scale = MAX_DIM / Math.max(w, h)
          w = Math.round(w * scale)
          h = Math.round(h * scale)
        }

        const canvas = document.createElement("canvas")
        canvas.width = w
        canvas.height = h
        canvas.getContext("2d").drawImage(img, 0, 0, w, h)

        canvas.toBlob((blob) => {
          URL.revokeObjectURL(objectUrl)
          resolve(blob ? new File([blob], jpegName, { type: "image/jpeg" }) : null)
        }, "image/jpeg", 0.92)
      }

      img.onerror = () => { clearTimeout(timer); URL.revokeObjectURL(objectUrl); resolve(null) }
      img.src = objectUrl
    })
  }

  // 既存画像のトリミング変更
  async reopenCropper(event) {
    event.preventDefault()
    // 新規ファイル選択中ならそのObjectURLを使う（元画像でズームアウト可能）
    const imageUrl = this.selectedFileUrl || this.existingImageUrlValue
    if (!imageUrl) return

    const existingCropData = this.cropDataFieldTarget.value
      ? JSON.parse(this.cropDataFieldTarget.value)
      : null
    await this.openModal(imageUrl, existingCropData)
  }

  async openModal(imageUrl, initialCropData) {
    this.cropping = true
    this._autoCropApplied = false
    this._manuallyAdjusted = false
    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")

    // S3等のcross-origin画像かどうか判定
    // blob: URLはローカルファイルなのでCORS不要
    this._isCrossOrigin = !imageUrl.startsWith("blob:")

    // cropData適用時は画像をプリロードしてブラウザキャッシュに入れる
    // （未キャッシュだとcropper-imageのtransformが中間寸法で発火してしまう）
    // crossorigin属性を付けてCORS対応キャッシュエントリを作成
    // （cropper-imageのcrossorigin属性と一致させないとキャッシュミスが起きる）
    if (initialCropData && this._isCrossOrigin) {
      this.showLoading("画像を読み込み中...")
      await new Promise((resolve) => {
        const img = new Image()
        img.crossOrigin = "anonymous"
        img.onload = resolve
        img.onerror = resolve
        img.src = imageUrl
      })
      this.hideLoading()
    }

    // Cropper.js v2 Custom Elementsを組み立て
    this.cropperAreaTarget.innerHTML = ""

    const canvas = document.createElement("cropper-canvas")
    canvas.setAttribute("background", "")
    canvas.setAttribute("scale-step", "0.1")
    canvas.style.width = "100%"
    canvas.style.height = "100%"

    const image = document.createElement("cropper-image")
    image.setAttribute("src", imageUrl)
    image.setAttribute("alt", "トリミング")
    image.setAttribute("scalable", "")
    image.setAttribute("translatable", "")
    image.setAttribute("slottable", "false")
    image.setAttribute("initial-center-size", "contain")
    // S3等のcross-origin画像でcanvas exportを可能にする
    // （S3バケットにCORS設定が必要）
    if (this._isCrossOrigin) {
      image.setAttribute("crossorigin", "anonymous")
    }

    const shade = document.createElement("cropper-shade")
    shade.setAttribute("hidden", "")

    const selection = document.createElement("cropper-selection")
    selection.setAttribute("aspect-ratio", String(ASPECT_RATIO))
    // 既存cropデータがある場合はinitial-coverageを設定しない
    // （Cropper.jsの初期配置がapplyCropDataを上書きしてしまうため）
    if (!initialCropData) {
      selection.setAttribute("initial-coverage", "0.8")
    }
    selection.setAttribute("movable", "")
    selection.setAttribute("resizable", "")
    selection.setAttribute("outlined", "")

    // 内部要素
    const grid = document.createElement("cropper-grid")
    grid.setAttribute("role", "grid")
    grid.setAttribute("covered", "")

    const crosshair = document.createElement("cropper-crosshair")
    crosshair.setAttribute("centered", "")

    const moveHandle = document.createElement("cropper-handle")
    moveHandle.setAttribute("action", "move")
    moveHandle.setAttribute("theme-color", "rgba(255, 255, 255, 0.35)")

    // リサイズハンドル
    const directions = ["n-resize", "e-resize", "s-resize", "w-resize", "ne-resize", "nw-resize", "se-resize", "sw-resize"]
    const handles = directions.map(dir => {
      const h = document.createElement("cropper-handle")
      h.setAttribute("action", dir)
      return h
    })

    selection.append(grid, crosshair, moveHandle, ...handles)
    canvas.append(image, shade, selection)
    this.cropperAreaTarget.appendChild(canvas)

    this.cropperCanvas = canvas
    this.cropperSelection = selection
    this.cropperImage = image

    // ユーザーがドラッグ/リサイズしたら手動調整フラグを立てる
    selection.addEventListener("pointerdown", () => {
      if (this._autoCropApplied) this._manuallyAdjusted = true
    })

    // 画像読み込み完了後にAIオートクロップ or 既存crop適用
    image.addEventListener("transform", () => {
      if (this._initialized) return
      this._initialized = true

      if (initialCropData) {
        this.applyCropData(initialCropData)
      } else if (this.autoCropEnabledValue && this.autoCropUrlValue) {
        this.requestAutoCrop()
      }
    }, { once: true })
  }

  applyCropData(cropData) {
    // 正規化座標からCropper.jsのピクセル座標に変換
    if (!this.cropperImage || !this.cropperSelection) return

    // 画像レイアウトが安定するまでリトライ（最大20回、50ms間隔）
    // 初回表示時、transform直後はgetBoundingClientRectの値がまだ変動中のため
    // 連続2回で寸法が安定（差1px以下）してから適用する
    let attempts = 0
    const maxAttempts = 20
    let lastW = 0
    let lastH = 0

    const tryApply = () => {
      attempts++
      const canvas = this.cropperCanvas
      if (!canvas || !this.cropperImage || !this.cropperSelection) return

      const canvasRect = canvas.getBoundingClientRect()
      const imgRect = this.cropperImage.getBoundingClientRect()

      const displayW = imgRect.width
      const displayH = imgRect.height

      // サイズゼロ or まだ変動中ならリトライ
      const stable = displayW > 0 && displayH > 0 &&
        Math.abs(displayW - lastW) <= 1 && Math.abs(displayH - lastH) <= 1
      if (!stable && attempts < maxAttempts) {
        lastW = displayW
        lastH = displayH
        setTimeout(tryApply, 50)
        return
      }
      if (displayW <= 0 || displayH <= 0) return

      const sx = cropData.x * displayW + (imgRect.left - canvasRect.left)
      const sy = cropData.y * displayH + (imgRect.top - canvasRect.top)
      const sw = cropData.width * displayW
      const sh = cropData.height * displayH

      this.cropperSelection.width = sw
      this.cropperSelection.height = sh
      this.cropperSelection.x = sx
      this.cropperSelection.y = sy
    }

    // 初回のレイアウト安定を待つ（transform直後はまだ変動中）
    requestAnimationFrame(() => setTimeout(tryApply, 100))
  }

  async requestAutoCrop() {
    if (!this.selectedFile) return

    this.showLoading("自動クロップ中...")

    const formData = new FormData()
    formData.append("photo", this.selectedFile)

    try {
      const response = await fetch(this.autoCropUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: formData
      })

      if (!response.ok) return

      const data = await response.json()
      if (data.focal_x !== undefined && data.focal_y !== undefined) {
        this.applyFocalPoint(data.focal_x, data.focal_y)
      }
    } catch (e) {
      console.warn("AI auto crop failed:", e)
    } finally {
      this.hideLoading()
    }
  }

  showLoading(message) {
    if (!this.hasLoadingTarget) return
    this.loadingTarget.classList.remove("hidden")
    if (message) {
      const span = this.loadingTarget.querySelector("span")
      if (span) span.textContent = message
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
  }

  applyFocalPoint(focalX, focalY) {
    if (!this.cropperImage || !this.cropperSelection) return
    this._autoCropApplied = true
    this._manuallyAdjusted = false

    requestAnimationFrame(() => {
      const canvas = this.cropperCanvas
      if (!canvas) return

      const canvasRect = canvas.getBoundingClientRect()
      const imgRect = this.cropperImage.getBoundingClientRect()

      const displayW = imgRect.width
      const displayH = imgRect.height
      if (displayW <= 0 || displayH <= 0) return

      const offsetX = imgRect.left - canvasRect.left
      const offsetY = imgRect.top - canvasRect.top

      // 料理にフォーカス: 画像の35%をカバーする選択範囲にズーム
      const zoomCoverage = 0.35
      let selH = displayH * zoomCoverage
      let selW = selH * ASPECT_RATIO

      // 画像幅を超える場合は幅に合わせる
      if (selW > displayW) {
        selW = displayW
        selH = selW / ASPECT_RATIO
      }

      // フォーカルポイントを中心にする
      let newX = focalX * displayW + offsetX - selW / 2
      let newY = focalY * displayH + offsetY - selH / 2

      // 画像範囲内に収める
      newX = Math.max(offsetX, Math.min(newX, offsetX + displayW - selW))
      newY = Math.max(offsetY, Math.min(newY, offsetY + displayH - selH))

      this.cropperSelection.width = selW
      this.cropperSelection.height = selH
      this.cropperSelection.x = newX
      this.cropperSelection.y = newY
    })
  }

  // 決定
  async confirm() {
    if (!this.cropperSelection) return

    try {
      // crop座標を正規化して保存
      this.saveCropData()
      this._pendingNewFile = false

      const cropData = JSON.parse(this.cropDataFieldTarget.value)
      const imageUrl = this.selectedFileUrl || this.existingImageUrlValue

      // プレビュー用にCanvasを生成（cross-originで失敗する場合はCSSフォールバック）
      let previewUrl = null
      try {
        const canvas = await this.cropperSelection.$toCanvas({ width: 800, height: 600 })
        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob((b) => b ? resolve(b) : reject(new Error("toBlob returned null")), "image/jpeg", 0.90)
        })
        previewUrl = URL.createObjectURL(blob)
      } catch (canvasErr) {
        // S3/CDN等のcross-origin画像ではtainted canvasでexport不可
        // CSSベースのプレビューにフォールバック
        console.error("[Cropper] Canvas export failed, falling back to CSS preview:", canvasErr.message)
      }

      // プレビュー更新
      if (this.hasPreviewTarget) {
        if (previewUrl) {
          this._showCanvasPreview(previewUrl)
        } else {
          this._showCssPreview(imageUrl, cropData)
        }
      }

      // 「トリミングを変更」ボタンを表示
      if (this.hasExistingImageTarget) {
        this.existingImageTarget.classList.remove("hidden")
      }

      // 他のコントローラー向けにイベントを発火
      this.dispatch("confirmed", {
        detail: {
          cropData: this.cropDataFieldTarget.value,
          previewUrl: previewUrl || imageUrl
        }
      })

      this.closeModal()
    } catch (e) {
      console.error("Crop failed:", e)
    }
  }

  // Canvas経由のプレビュー（ローカルファイルやCORS対応済みの場合）
  _showCanvasPreview(previewUrl) {
    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.src = previewUrl
      this.previewImageTarget.removeAttribute("style")
      this.previewTarget.classList.remove("hidden")
    } else {
      const img = document.createElement("img")
      img.src = previewUrl
      img.className = "w-32 h-24 object-cover rounded-lg border border-gray-200"
      this.previewTarget.innerHTML = ""
      this.previewTarget.appendChild(img)
      this.previewTarget.classList.remove("hidden")
    }
  }

  // CSSベースのプレビュー（cross-origin画像のフォールバック）
  // CSSクラスに依存せず、JSから直接スタイル設定（ビルド/パージの影響を受けない）
  _showCssPreview(imageUrl, cropData) {
    const wrapper = document.createElement("div")
    wrapper.className = "w-32 h-24 rounded-lg border border-gray-200 overflow-hidden relative cursor-pointer"
    wrapper.dataset.action = "click->cropper#reopenCropper"

    const img = document.createElement("img")
    img.src = imageUrl
    const scaleX = 1 / cropData.width
    const scaleY = 1 / cropData.height
    img.style.position = "absolute"
    img.style.width = `${scaleX * 100}%`
    img.style.height = `${scaleY * 100}%`
    img.style.left = `${-cropData.x * scaleX * 100}%`
    img.style.top = `${-cropData.y * scaleY * 100}%`

    wrapper.appendChild(img)
    this.previewTarget.innerHTML = ""
    this.previewTarget.appendChild(wrapper)
    this.previewTarget.classList.remove("hidden")
  }

  saveCropData() {
    if (!this.cropperImage || !this.cropperSelection) return

    const canvasRect = this.cropperCanvas.getBoundingClientRect()
    const imgRect = this.cropperImage.getBoundingClientRect()

    const displayW = imgRect.width
    const displayH = imgRect.height
    if (displayW <= 0 || displayH <= 0) return

    const offsetX = imgRect.left - canvasRect.left
    const offsetY = imgRect.top - canvasRect.top

    const sel = this.cropperSelection

    const cropData = {
      x: Math.max(0, (sel.x - offsetX) / displayW),
      y: Math.max(0, (sel.y - offsetY) / displayH),
      width: Math.min(1, sel.width / displayW),
      height: Math.min(1, sel.height / displayH),
      source: this._autoCropApplied ? "auto" : "manual",
      manually_adjusted: this._autoCropApplied ? !!this._manuallyAdjusted : true
    }

    // 範囲を0〜1にクランプ
    cropData.x = Math.max(0, Math.min(1, cropData.x))
    cropData.y = Math.max(0, Math.min(1, cropData.y))
    cropData.width = Math.max(0, Math.min(1 - cropData.x, cropData.width))
    cropData.height = Math.max(0, Math.min(1 - cropData.y, cropData.height))

    this.cropDataFieldTarget.value = JSON.stringify(cropData)
  }

  // キャンセル
  cancel() {
    // 新規ファイル選択中のキャンセル: file_fieldをクリア
    // （確定済みの再トリミングキャンセルでは元ファイルを維持）
    if (this._pendingNewFile) {
      this.fileInputTarget.value = ""
      this.selectedFile = null
      if (this.selectedFileUrl) {
        URL.revokeObjectURL(this.selectedFileUrl)
        this.selectedFileUrl = null
      }
      this._pendingNewFile = false
    }
    this.closeModal()
  }

  // リセット
  reset() {
    if (!this.cropperSelection) return
    this.cropperSelection.$center()
  }

  closeModal() {
    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
    this.cleanup()
  }

  cleanup() {
    this._initialized = false
    this.cropperCanvas = null
    this.cropperSelection = null
    this.cropperImage = null
    this.cropping = false
    if (this.hasCropperAreaTarget) {
      this.cropperAreaTarget.innerHTML = ""
    }
  }
}
